import { useState, useEffect, useCallback, useRef } from "react";
import { X, Check } from "lucide-react";

import { useTranslation } from 'react-i18next';
const NOTIFICATIONS = [
  { city: "S\u00e3o Paulo", country: "BR", action: "just activated their AI agent", time: "2 min ago", icon: "check" },
  { city: "Miami", country: "US", action: "just deployed Max OS Cloud", time: "5 min ago", icon: "fire" },
  { city: "Lisbon", country: "PT", action: "just connected WhatsApp", time: "8 min ago", icon: "check" },
  { city: "New York", country: "US", action: "just saved 15 hours/week", time: "12 min ago", icon: "fire" },
  { city: "London", country: "UK", action: "just scaled to 3 channels", time: "15 min ago", icon: "check" },
  { city: "Berlin", country: "DE", action: "just automated customer support", time: "18 min ago", icon: "check" },
  { city: "Toronto", country: "CA", action: "just deployed their first agent", time: "22 min ago", icon: "fire" },
  { city: "Sydney", country: "AU", action: "just went live with Max OS", time: "25 min ago", icon: "check" },
  { city: "Dubai", country: "AE", action: "just connected Telegram", time: "28 min ago", icon: "check" },
  { city: "Singapore", country: "SG", action: "just saved $4,200/month", time: "32 min ago", icon: "fire" },
];

function getRandomInterval() {
  return (15 + Math.random() * 15) * 1000; // 15-30s in ms
}

function getRandomNotification() {
  return NOTIFICATIONS[Math.floor(Math.random() * NOTIFICATIONS.length)];
}

export default function SocialProofNotifications() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [notification, setNotification] = useState(null);
  const [entering, setEntering] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [paused, setPaused] = useState(false);

  const dismissTimerRef = useRef(null);
  const scheduleTimerRef = useRef(null);
  const interactionTimerRef = useRef(null);

  const clearAllTimers = useCallback(() => {
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    if (scheduleTimerRef.current) clearTimeout(scheduleTimerRef.current);
    if (interactionTimerRef.current) clearTimeout(interactionTimerRef.current);
  }, []);

  const showNotification = useCallback(() => {
    setNotification(getRandomNotification());
    setVisible(true);
    setEntering(true);
    setExiting(false);

    // After entry animation, clear entering state
    setTimeout(() => setEntering(false), 300);

    // Auto-dismiss after 5 seconds
    dismissTimerRef.current = setTimeout(() => {
      setExiting(true);
      setTimeout(() => {
        setVisible(false);
        setExiting(false);
        setNotification(null);
      }, 300);
    }, 5000);
  }, []);

  const scheduleNext = useCallback(() => {
    clearAllTimers();
    if (paused) return;
    const interval = getRandomInterval();
    scheduleTimerRef.current = setTimeout(() => {
      showNotification();
    }, interval);
  }, [paused, clearAllTimers, showNotification]);

  // Start the loop and handle pause/resume on user interaction
  useEffect(() => {
    // Initial delay before first notification
    scheduleTimerRef.current = setTimeout(() => {
      showNotification();
    }, 3000);

    const handleInteraction = () => {
      setPaused(true);
      clearAllTimers();
      if (interactionTimerRef.current) clearTimeout(interactionTimerRef.current);
      // Resume after 3 seconds of no interaction
      interactionTimerRef.current = setTimeout(() => {
        setPaused(false);
        scheduleNext();
      }, 3000);
    };

    window.addEventListener("scroll", handleInteraction, { passive: true });
    window.addEventListener("mousemove", handleInteraction, { passive: true });

    return () => {
      clearAllTimers();
      window.removeEventListener("scroll", handleInteraction);
      window.removeEventListener("mousemove", handleInteraction);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Schedule next after each notification completes
  useEffect(() => {
    if (!visible && !exiting) {
      scheduleNext();
    }
  }, [visible, exiting, scheduleNext]);

  const handleClose = () => {
    clearAllTimers();
    setExiting(true);
    setTimeout(() => {
      setVisible(false);
      setExiting(false);
      setNotification(null);
    }, 300);
  };

  if (!visible || !notification) return null;

  return (
    <div
      className={`fixed bottom-6 left-6 z-[35] max-w-[320px] transition-all duration-300 ${
        entering
          ? "opacity-0 -translate-x-8"
          : exiting
          ? "opacity-0 -translate-x-8"
          : "opacity-100 translate-x-0"
      }`}
      role="status"
      aria-live="polite"
    >
      <div className="relative rounded-lg border border-zinc-800 bg-zinc-950/95 px-4 py-3 shadow-2xl shadow-black/40 backdrop-blur-sm">
        {/* Close button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-2 top-2 rounded-full p-1 text-zinc-600 transition-colors hover:text-zinc-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-500"
          aria-label={t('social_proof_notifications.dismiss_notification')}
        >
          <X className="h-3 w-3" />
        </button>

        <div className="flex items-start gap-3 pr-4">
          {/* Icon */}
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-500/20">
            {notification.icon === "fire" ? (
              <span className="text-xs">{"\uD83D\uDD25"}</span>
            ) : (
              <Check className="h-3 w-3 text-amber-500" />
            )}
          </span>

          {/* Content */}
          <div className="min-w-0">
            <p className="font-mono text-xs leading-relaxed text-zinc-200">
              <span className="text-amber-400">{notification.city}, {notification.country}</span>{" "}
              {notification.action}
            </p>
            <p className="mt-1 font-mono text-[10px] text-zinc-500">
              {notification.time}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
